import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  TextField,
  List,
  ListItem,
  Avatar,
  Fab,
  Slide,
  Fade,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Zoom,
  Grow,
  Alert,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Add as AddIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,

  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import chatService from '../services/chatService';
import ReactMarkdown from 'react-markdown';
import TypewriterText from './common/TypewriterText';

// Seuil pour détecter l'ouverture du clavier virtuel (en pixels)
const KEYBOARD_THRESHOLD = 150;

// Animations modernes
const float = keyframes`
  0%, 100% { transform: translateY(0px) scale(1); }
  50% { transform: translateY(-8px) scale(1.05); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const ripple = keyframes`
  0% { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(2.5); opacity: 0; }
`;

// Animation pour l'icône de discussion
const chatBubble = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.1) rotate(-2deg); }
  75% { transform: scale(1.1) rotate(2deg); }
`;

// Style pour le bouton intégré dans le menu - EXACTEMENT identique au bouton "Nouvelle réunion"
const MenuChatButton = styled(ListItemButton)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(10), // Positionné au-dessus du bouton "Nouvelle réunion"
  left: theme.spacing(2),
  width: `calc(280px - ${theme.spacing(4)})`, // Largeur du drawer (280px) moins les marges
  margin: 0,
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`, // py: 1 comme le bouton original
  borderRadius: theme.spacing(3), // borderRadius: 3 comme le bouton original
  background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 50%, #7986CB 100%)', // Dégradé exact avec 3 couleurs
  boxShadow: `0 6px 20px rgba(63, 81, 181, 0.25), 
             0 3px 10px rgba(63, 81, 181, 0.15),
             inset 0 1px 0 rgba(255,255,255,0.2)`, // BoxShadow exact
  border: '1px solid rgba(255,255,255,0.1)', // Border exact
  color: 'white',
  zIndex: 1300,
  minHeight: 'auto',
  textTransform: 'none',
  fontWeight: 600, // fontWeight: 600 comme le bouton original
  fontSize: '0.9rem', // fontSize: '0.9rem' comme le bouton original
  minWidth: 'auto',
  justifyContent: 'center', // Centrer le contenu comme le bouton "Nouvelle réunion"
  gap: theme.spacing(1),
  overflow: 'hidden', // Pour l'effet shimmer
  // Effet shimmer identique
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.6s',
  },
  '&:hover': {
    background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 50%, #5C6BC0 100%)', // Hover exact
    transform: 'translateY(-2px) scale(1.01)', // Transform exact
    boxShadow: `0 8px 25px rgba(63, 81, 181, 0.35), 
               0 4px 15px rgba(63, 81, 181, 0.25),
               inset 0 1px 0 rgba(255,255,255,0.3)`, // BoxShadow hover exact
    '&::before': {
      left: '100%' // Effet shimmer au hover
    }
  },
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // Transition exacte
  [theme.breakpoints.down('md')]: {
    display: 'none', // Masquer sur tablette/mobile pour éviter conflits
  }
}));

// Styled Components avec design Vision Pro
const ChatButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  left: theme.spacing(4), // Positionnement à gauche
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4), 0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  animation: `${float} 3s ease-in-out infinite`,
  zIndex: 1200, // Sous le menu principal mais au-dessus du contenu
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%)',
    transform: 'scale(1.1)',
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.5), 0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover::before': {
    opacity: 1,
  },
  [theme.breakpoints.up('md')]: {
    display: 'none', // Masquer sur desktop car on utilise MenuChatButton
  },
  [theme.breakpoints.down('md')]: {
    // Sur tablette et mobile, repositionner à gauche pour éviter le conflit avec scroll-to-top
    left: theme.spacing(2),
    right: 'auto',
    bottom: theme.spacing(10), // Plus haut pour éviter le conflit
  }
}));

const ChatContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'dynamicHeight',
})(({ theme, dynamicHeight }) => ({
  position: 'fixed',
  bottom: theme.spacing(18), // Plus haut pour laisser de l'espace au bouton menu
  left: theme.spacing(4),
  width: 420,
  height: 600,
  display: 'flex',
  flexDirection: 'column',
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(30px) saturate(200%)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 10px 30px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  zIndex: 1400,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
    animation: `${shimmer} 3s infinite`,
  },
  [theme.breakpoints.down('md')]: {
    // Sur tablette et mobile, repositionner à droite et plus haut
    left: 'auto',
    right: theme.spacing(2),
    width: 380,
    bottom: theme.spacing(18), // Plus haut pour éviter scroll-to-top
  },
  [theme.breakpoints.down('sm')]: {
    width: '100vw',
    height: dynamicHeight || '100dvh', // Hauteur dynamique pour Safari iOS avec fallback
    maxHeight: 'none',
    bottom: 0,
    right: 0,
    left: 0,
    borderRadius: 0,
  }
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
  borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})(({ theme, isUser }) => ({
  maxWidth: '75%',
  padding: theme.spacing(1.5, 2),
  borderRadius: isUser ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
  background: isUser
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    : 'rgba(241, 243, 245, 0.9)',
  color: isUser ? 'white' : theme.palette.text.primary,
  backdropFilter: 'blur(10px)',
  boxShadow: isUser
    ? '0 4px 12px rgba(99, 102, 241, 0.3)'
    : '0 2px 8px rgba(0, 0, 0, 0.05)',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  position: 'relative',
  '& .markdown': {
    '& p': {
      margin: 0,
      lineHeight: 1.5,
    },
    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '20px',
    },
    '& li': {
      marginBottom: '4px',
    },
    '& code': {
      background: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '0.9em',
    }
  }
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
  background: 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(20px)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '20px',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    '& fieldset': {
      borderColor: 'rgba(99, 102, 241, 0.2)',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(99, 102, 241, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6366f1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '16px', // Évite le zoom automatique sur iOS
  },
}));

const LoadingDots = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '4px',
  '& .dot': {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#6366f1',
    animation: `${pulse} 1.4s ease-in-out infinite`,
    '&:nth-of-type(2)': {
      animationDelay: '0.2s',
    },
    '&:nth-of-type(3)': {
      animationDelay: '0.4s',
    },
  }
}));

const ChatBot = ({ meeting, transcription }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [error, setError] = useState(null);
  const [dynamicHeight, setDynamicHeight] = useState('100dvh');
  const [typingMessageId, setTypingMessageId] = useState(null);
  const [animatedMessages, setAnimatedMessages] = useState(new Set());
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const lastValidHeight = useRef('100dvh'); // Conserver la dernière hauteur valide

  // Gestion de la hauteur dynamique pour Safari iOS
  useEffect(() => {
    const updateHeight = () => {
      if (!window.visualViewport) {
        setDynamicHeight('100dvh'); // fallback pour les navigateurs qui ne gèrent pas visualViewport
        return;
      }
      
      const viewportHeight = window.visualViewport.height;
      const isKeyboard = window.innerHeight - viewportHeight > KEYBOARD_THRESHOLD;
      
      if (!isKeyboard) {
        // Pas de clavier détecté : mettre à jour normalement
        lastValidHeight.current = `${viewportHeight}px`;
        setDynamicHeight(lastValidHeight.current);
      } else {
        // Clavier détecté : conserver la dernière hauteur valide
        setDynamicHeight(lastValidHeight.current);
      }
    };

    updateHeight(); // ajustement initial
    window.visualViewport?.addEventListener('resize', updateHeight);
    return () => {
      window.visualViewport?.removeEventListener('resize', updateHeight);
    };
  }, []);

  // Scroll automatique vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Charger une conversation existante
  const loadConversation = useCallback(async (conversationId) => {
    try {
      setCurrentConversationId(conversationId);
      setShowHistory(false);
      setAnimatedMessages(new Set()); // Réinitialiser les messages animés
      setTypingMessageId(null); // Réinitialiser l'animation en cours
      
      // S'abonner aux mises à jour
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      
      unsubscribeRef.current = chatService.onMessagesUpdate(conversationId, (newMessages) => {
        setMessages(newMessages);
      });
    } catch (error) {
      addSystemNotification('Erreur lors du chargement de la conversation:', 'error');
      setError('Impossible de charger la conversation');
    }
  }, []);

  // Charger les conversations existantes uniquement
  const loadConversations = useCallback(async () => {
    if (!currentUser?.uid || !meeting?.id) return;
    
    try {
      const convs = await chatService.getConversations(currentUser.uid, meeting.id);
      setConversations(convs);
      return convs;
    } catch (error) {
      addSystemNotification('Erreur lors du chargement des conversations:', 'error');
      return [];
    }
  }, [currentUser?.uid, meeting?.id]);

  // État pour contrôler l'initialisation
  const [isInitialized, setIsInitialized] = useState(false);

  // Utiliser useRef pour éviter les rechargements multiples
  const initializationRef = useRef(false);
  const userMeetingKey = useRef('');

  // Charger automatiquement la dernière conversation au démarrage
  useEffect(() => {
    const currentKey = `${currentUser?.uid}-${meeting?.id}`;
    
    // Si l'utilisateur ou la réunion change, réinitialiser
    if (userMeetingKey.current !== currentKey) {
      userMeetingKey.current = currentKey;
      initializationRef.current = false;
      setIsInitialized(false);
    }
    
    let isMounted = true; // Flag pour éviter les setState après unmount
    
    const initializeChat = async () => {
      if (!currentUser?.uid || !meeting?.id || initializationRef.current || !isMounted) return;
      
      initializationRef.current = true; // Marquer comme en cours d'initialisation
      
      try {
        const convs = await chatService.getConversations(currentUser.uid, meeting.id);
        
        if (!isMounted) return; // Vérifier si le composant est toujours monté
        
        setConversations(convs);
        
        // Si il y a des conversations, charger la plus récente
        if (convs.length > 0) {
          const latestConversation = convs[0]; // Les conversations sont triées par createdAt desc
          
          if (isMounted) {
            // Charger directement sans dépendre de loadConversation
            setCurrentConversationId(latestConversation.id);
            
            // S'abonner aux mises à jour
            if (unsubscribeRef.current) {
              unsubscribeRef.current();
            }
            
            unsubscribeRef.current = chatService.onMessagesUpdate(latestConversation.id, (newMessages) => {
              if (isMounted) {
                setMessages(newMessages);
              }
            });
          }
        }
        
        if (isMounted) {
        setIsInitialized(true);
        }
      } catch (error) {
        addSystemNotification("❌ Erreur lors de l'initialisation", 'error');
        if (isMounted) {
        setIsInitialized(true);
        }
      }
    };

    if (currentUser?.uid && meeting?.id && !initializationRef.current) {
      // Délai pour éviter les appels multiples
      const timeoutId = setTimeout(() => {
        if (!initializationRef.current) {
    initializeChat();
        }
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        isMounted = false;
      };
    }
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid, meeting?.id]);

  // Recharger les conversations quand le chat s'ouvre (si pas encore initialisé)
  useEffect(() => {
    if (isOpen && !isInitialized && !initializationRef.current && currentUser?.uid && meeting?.id) {
      loadConversations();
    }
  }, [isOpen, isInitialized, currentUser?.uid, meeting?.id, loadConversations]);

  // Créer une nouvelle conversation
  const startNewConversation = async () => {
    if (!currentUser?.uid || !meeting?.id) return;
    
    try {
      const conversationId = await chatService.createConversation(
        currentUser.uid,
        meeting.id,
        meeting.title
      );
      
      setCurrentConversationId(conversationId);
      setMessages([]);
      setAnimatedMessages(new Set()); // Réinitialiser les messages animés
      setTypingMessageId(null); // Réinitialiser l'animation en cours
      
      // Ajouter le message d'accueil
      const welcomeMessage = {
        role: 'assistant',
        content: chatService.getWelcomeMessage(meeting.title),
        isWelcome: true // Marquer comme message de bienvenue
      };
      
      await chatService.addMessage(conversationId, welcomeMessage);
      setTypingMessageId(welcomeMessage.id || 'welcome'); // Marquer pour l'effet de frappe
      
      // S'abonner aux mises à jour
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      
      unsubscribeRef.current = chatService.onMessagesUpdate(conversationId, (newMessages) => {
        setMessages(newMessages);
      });
      
      await loadConversations();
    } catch (error) {
      addSystemNotification('Erreur lors de la création de la conversation:', 'error');
      setError('Impossible de créer une nouvelle conversation');
    }
  };

  // Supprimer une conversation
  const deleteConversation = async (conversationId) => {
    try {
      await chatService.deleteConversation(conversationId);
      
      if (conversationId === currentConversationId) {
        setCurrentConversationId(null);
        setMessages([]);
      }
      
      await loadConversations();
      setShowHistory(false);
    } catch (error) {
      addSystemNotification('Erreur lors de la suppression:', 'error');
      setError('Impossible de supprimer la conversation');
    }
  };

  // Envoyer un message
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || !currentConversationId) return;
    
    const userMessage = {
      role: 'user',
      content: inputValue.trim()
    };
    
    setInputValue('');
    setIsLoading(true);
    setError(null);
    
    try {
      // Ajouter le message utilisateur
      await chatService.addMessage(currentConversationId, userMessage);
      
      // Préparer la transcription
      let transcriptionText = '';
      if (transcription?.utterances) {
        transcriptionText = transcription.utterances
          .map(u => `${u.speaker}: ${u.text}`)
          .join('\n\n');
      } else if (transcription?.fullText) {
        transcriptionText = transcription.fullText;
      }
      
      // Obtenir la réponse de l'IA
      const response = await chatService.sendToOpenAI(
        [...messages, userMessage],
        transcriptionText
      );
      
      // Ajouter la réponse de l'assistant
      const assistantMessage = {
        role: 'assistant',
        content: response,
        id: Date.now() // Ajouter un ID pour le tracking
      };
      
      await chatService.addMessage(currentConversationId, assistantMessage);
      setTypingMessageId(assistantMessage.id); // Marquer pour l'effet de frappe
    } catch (error) {
      addSystemNotification("Erreur lors de l'envoi du message", 'error');
      setError(error.message || 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  // Gérer l'ouverture/fermeture
  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    // Ne pas créer automatiquement une nouvelle conversation
    // L'historique sera chargé par loadConversations()
  };

  // Nettoyer lors du démontage
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return (
    <>
      {/* Bouton intégré dans le menu (desktop) */}
      {isDesktop && (
        <Zoom in={!isOpen} timeout={300}>
          <MenuChatButton
            onClick={handleToggleChat}
            sx={{ display: isOpen ? 'none' : 'flex' }}
          >
            {/* Icône avec badge - style exact du bouton "Nouvelle réunion" */}
            <Box sx={{ 
              position: 'relative',
              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
              fontSize: '1.1rem' // fontSize exact du bouton original
            }}>
              <ChatIcon sx={{ 
                fontSize: '1.1rem', // Même taille exacte que l'icône du bouton "Nouvelle réunion"
                animation: `${chatBubble} 2s ease-in-out infinite`
              }} />
              <Box
                sx={{
                  position: 'absolute',
                  top: -3,
                  right: -3,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#4CAF50',
                  border: '1px solid white',
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
            </Box>
            {/* Texte simple */}
            Poser une question
          </MenuChatButton>
        </Zoom>
      )}

      {/* Bouton flottant (mobile/tablette) */}
      <Zoom in={!isOpen} timeout={300}>
        <ChatButton
          color="primary"
          onClick={handleToggleChat}
          sx={{ display: isOpen ? 'none' : 'flex' }}
        >
          <Box sx={{ position: 'relative' }}>
            <AutoAwesomeIcon sx={{ fontSize: 28 }} />
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: '#10b981',
                border: '2px solid white',
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            />
          </Box>
        </ChatButton>
      </Zoom>

      {/* Container du chat */}
      <Slide direction={isMobile ? 'up' : 'right'} in={isOpen} mountOnEnter unmountOnExit>
        <ChatContainer elevation={0} dynamicHeight={dynamicHeight}>
          {/* Header */}
          <ChatHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  width: 36,
                  height: 36,
                }}
              >
                <ChatIcon sx={{ animation: `${chatBubble} 2s ease-in-out infinite` }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Poser une question
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Assistant intelligent
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Nouvelle conversation">
                <IconButton
                  size="small"
                  onClick={startNewConversation}
                  sx={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    '&:hover': { background: 'rgba(99, 102, 241, 0.2)' }
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={`Historique (${conversations.length} conversation${conversations.length > 1 ? 's' : ''})`}>
                <IconButton
                  size="small"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    '&:hover': { background: 'rgba(99, 102, 241, 0.2)' },
                    position: 'relative'
                  }}
                >
                  <HistoryIcon fontSize="small" />
                  {conversations.length > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 16,
                        height: 16,
                        background: '#10b981',
                        color: 'white',
                        borderRadius: '50%',
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        border: '2px solid white',
                      }}
                    >
                      {conversations.length > 9 ? '9+' : conversations.length}
                    </Box>
                  )}
                </IconButton>
              </Tooltip>
              
              <IconButton
                size="small"
                onClick={handleToggleChat}
                sx={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  '&:hover': { background: 'rgba(239, 68, 68, 0.2)' }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </ChatHeader>

          {/* Zone de messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              background: 'linear-gradient(to bottom, transparent, rgba(249, 250, 251, 0.5))',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '3px',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.2)',
                },
              },
            }}
          >
            {/* Affichage conditionnel selon l'état */}
            {!isInitialized ? (
              // Chargement initial
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress sx={{ mb: 2, color: '#6366f1' }} />
                <Typography variant="body2" color="text.secondary">
                  Chargement des conversations...
                </Typography>
              </Box>
            ) : !currentConversationId ? (
              // État initial - pas de conversation active
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <ChatIcon sx={{ 
                    fontSize: 40,
                    animation: `${chatBubble} 2s ease-in-out infinite`
                  }} />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  Poser une question
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Je suis là pour vous aider à analyser votre réunion.
                  {conversations.length > 0 
                    ? 'Sélectionnez une conversation dans l\'historique ou créez-en une nouvelle !'
                    : 'Commencez une conversation pour poser vos questions !'
                  }
                </Typography>
              </Box>
            ) : (
              // Messages de la conversation active
              messages.map((message, index) => (
                <Fade in key={message.id || index} timeout={300}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-end',
                      gap: 1,
                    }}
                  >
                    {message.role === 'assistant' && (
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        }}
                      >
                        <ChatIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                    )}
                    
                    <MessageBubble isUser={message.role === 'user'}>
                      {message.role === 'user' ? (
                        <Typography variant="body2">{message.content}</Typography>
                      ) : (
                        // Utiliser TypewriterText pour les messages de bienvenue et les nouvelles réponses
                        (message.isWelcome || message.id === typingMessageId) && !animatedMessages.has(message.id || message.content) ? (
                          <TypewriterText 
                            text={message.content} 
                            speed={message.isWelcome ? 25 : 15}
                            delay={message.isWelcome ? 800 : 300}
                            cursorColor="#6366f1"
                            onComplete={() => {
                              // Marquer ce message comme animé
                              setAnimatedMessages(prev => new Set(prev).add(message.id || message.content));
                              
                              if (message.id === typingMessageId) {
                                // Ajouter un délai pour éviter le clignotement
                                setTimeout(() => {
                                  setTypingMessageId(null);
                                }, 100);
                              }
                            }}
                          >
                            <Box className="markdown">
                              <ReactMarkdown />
                            </Box>
                          </TypewriterText>
                        ) : (
                          <Box className="markdown">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </Box>
                        )
                      )}
                    </MessageBubble>
                  </Box>
                </Fade>
              ))
            )}
            
            {isLoading && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  }}
                >
                  <ChatIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <MessageBubble isUser={false}>
                  <LoadingDots>
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                  </LoadingDots>
                </MessageBubble>
              </Box>
            )}
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  borderRadius: 2,
                  background: 'rgba(254, 226, 226, 0.8)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {error}
              </Alert>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Zone de saisie */}
          <InputContainer>
            {!currentConversationId ? (
              // Afficher un bouton pour commencer si aucune conversation
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={startNewConversation}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                    },
                  }}
                >
                  Commencer une conversation
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <StyledTextField
                  fullWidth
                  placeholder="Posez votre question sur la réunion..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={isLoading}
                  size="small"
                  inputRef={inputRef}
                />
                
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                    },
                    '&:disabled': {
                      background: 'rgba(0, 0, 0, 0.1)',
                      color: 'rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            )}
          </InputContainer>

          {/* Menu historique */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            slotProps={{
              root: {
                sx: {
                  zIndex: 1500, // Plus élevé que le ChatContainer (1400)
                }
              }
            }}
            PaperProps={{
              sx: {
                width: 420, // Même largeur que le ChatContainer
                maxWidth: 420,
                maxHeight: 400,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.08)',
                marginTop: 1,
                zIndex: 1500,
                [theme => theme.breakpoints.down('md')]: {
                  width: 380, // Même largeur que le ChatContainer sur mobile
                  maxWidth: 380,
                },
                [theme => theme.breakpoints.down('sm')]: {
                  width: '90vw', // Responsive sur petit écran
                  maxWidth: '90vw',
                }
              }
            }}
          >
            {/* Header avec bouton de fermeture */}
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#3F51B5' }}>
                Conversations précédentes
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => setAnchorEl(null)}
                sx={{ 
                  color: 'rgba(0, 0, 0, 0.4)',
                  '&:hover': { 
                    color: 'rgba(0, 0, 0, 0.6)',
                    background: 'rgba(0, 0, 0, 0.05)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            
            {conversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Aucune conversation
                </Typography>
              </Box>
            ) : (
              <>
                {conversations.map((conv) => (
                  <MenuItem
                    key={conv.id}
                    onClick={() => {
                      loadConversation(conv.id);
                      setAnchorEl(null);
                    }}
                    selected={conv.id === currentConversationId}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      py: 1.5,
                      px: 2,
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&.Mui-selected': {
                        background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.1) 0%, rgba(92, 107, 192, 0.08) 100%)',
                        border: '1px solid rgba(63, 81, 181, 0.2)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.15) 0%, rgba(92, 107, 192, 0.12) 100%)',
                        }
                      },
                      '&:hover:not(.Mui-selected)': {
                        background: 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: conv.id === currentConversationId ? 600 : 400,
                          color: conv.id === currentConversationId ? '#3F51B5' : 'inherit',
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {conv.lastMessage || 'Nouvelle conversation'}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.75rem'
                        }}
                      >
                        {conv.createdAt?.toDate?.().toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) || 'Date inconnue'}
                      </Typography>
                    </Box>
                    
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(conv.id);
                      }}
                      sx={{ 
                        ml: 1,
                        color: 'rgba(0, 0, 0, 0.4)',
                        '&:hover': { 
                          color: '#f44336',
                          background: 'rgba(244, 67, 54, 0.1)'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </MenuItem>
                ))}
                
                <Divider sx={{ my: 1, mx: 2 }} />
                
                <MenuItem
                  onClick={() => {
                    startNewConversation();
                    setAnchorEl(null);
                  }}
                  sx={{
                    justifyContent: 'center',
                    color: '#3F51B5',
                    fontWeight: 600,
                    py: 1.5,
                    mx: 2,
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.08) 0%, rgba(92, 107, 192, 0.05) 100%)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  <AddIcon fontSize="small" sx={{ mr: 1 }} />
                  Nouvelle conversation
                </MenuItem>
              </>
            )}
          </Menu>
        </ChatContainer>
      </Slide>
    </>
  );
};

export default ChatBot;